import { Link } from "react-router-dom";
import { Mail, MapPin, Shield, MessageCircle, Car } from "lucide-react";
import { useSite } from "../lib/SiteContext";
import { waLink } from "../lib/api";

const Footer = () => {
  const { content, tours } = useSite();
  const settings = content?.settings;
  const year = new Date().getFullYear();

  return (
    <footer className="bg-pine-dark text-white" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3">
              <img src="/assets/aloha-logo.png" alt="Aloha Corfu Tours logo" className="h-11 w-11 rounded-full bg-white" />
              <span className="font-display text-xl font-semibold">Aloha Corfu Tours</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/70">
              {settings?.footer_text || "Private tours, sightseeing and experiences across the beautiful island of Corfu, Greece."}
            </p>
            <div className="mt-5 flex items-center gap-2 text-sm text-white/70">
              <MapPin className="h-4 w-4 text-seafoam" /> Corfu, Greece
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-seafoam">Explore</h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><Link to="/tours" className="text-white/80 transition-colors hover:text-white" data-testid="footer-link-tours">All Tours</Link></li>
              <li><Link to="/customize" className="text-white/80 transition-colors hover:text-white" data-testid="footer-link-customize">Customize Your Tour</Link></li>
              <li><Link to="/gallery" className="text-white/80 transition-colors hover:text-white" data-testid="footer-link-gallery">Gallery</Link></li>
              <li><Link to="/about" className="text-white/80 transition-colors hover:text-white" data-testid="footer-link-about">About Us</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-seafoam">Popular Tours</h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              {tours.slice(0, 4).map((t) => (
                <li key={t.id}>
                  <Link to={`/tours/${t.slug}`} className="text-white/80 transition-colors hover:text-white" data-testid={`footer-tour-${t.slug}`}>
                    {t.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-seafoam">Contact</h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <a href={waLink(settings)} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 text-white/80 transition-colors hover:text-white" data-testid="footer-whatsapp-link">
                  <MessageCircle className="h-4 w-4 text-[#25D366]" /> +30 698 340 0353
                </a>
              </li>
              {settings?.contact_email && (
                <li>
                  <a href={`mailto:${settings.contact_email}`} className="inline-flex items-center gap-2 text-white/80 transition-colors hover:text-white" data-testid="footer-email-link">
                    <Mail className="h-4 w-4 text-seafoam" /> {settings.contact_email}
                  </a>
                </li>
              )}
              <li>
                <a href="https://alohacorfutransfers.com" target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 font-semibold text-white transition-colors hover:text-gold" data-testid="footer-book-transfer-link">
                  <Car className="h-4 w-4 text-gold" /> Book a Transfer →
                </a>
              </li>
              <li><Link to="/contact" className="text-white/80 transition-colors hover:text-white" data-testid="footer-link-contact">Contact Form</Link></li>
              <li><Link to="/faq" className="text-white/80 transition-colors hover:text-white" data-testid="footer-link-faq">Frequently Asked Questions</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-6 text-xs text-white/50 sm:flex-row">
          <p>© {year} Aloha Corfu Tours · Corfu, Greece. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="transition-colors hover:text-white" data-testid="footer-link-privacy">Privacy Policy</Link>
            <Link to="/admin/login" className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1.5 transition-colors hover:bg-white/10 hover:text-white" data-testid="footer-admin-link">
              <Shield className="h-3.5 w-3.5" /> Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
